/**
 * Shared zoom-at-cursor logic for main canvas and gallery.
 * Both canvases use this so wheel zoom behaves identically.
 */

/** Wheel zoom multiplier: scroll down = 0.9, scroll up = 1.1 */
export function getWheelZoomDelta(deltaY: number): number {
  return deltaY > 0 ? 0.9 : 1.1;
}

export interface ZoomAtPointParams {
  zoom: number;
  panX: number;
  panY: number;
  pointX: number;
  pointY: number;
  newZoom: number;
  minZoom: number;
  maxZoom: number;
}

export interface ZoomAtPointResult {
  zoom: number;
  panX: number;
  panY: number;
}

/**
 * Compute new zoom and pan so the point (pointX, pointY) stays under the cursor.
 * pointX/pointY are in "container center" space (same as pan).
 * Returns null if newZoom is unchanged (e.g. clamped to current).
 */
export function computeZoomAtPoint(params: ZoomAtPointParams): ZoomAtPointResult | null {
  const { zoom, panX, panY, pointX, pointY, newZoom, minZoom, maxZoom } = params;
  const clampedZoom = Math.max(minZoom, Math.min(maxZoom, newZoom));

  if (clampedZoom === zoom) {
    return null;
  }

  const canvasPointX = (pointX - panX) / zoom;
  const canvasPointY = (pointY - panY) / zoom;
  const newPanX = pointX - canvasPointX * clampedZoom;
  const newPanY = pointY - canvasPointY * clampedZoom;

  return {
    zoom: clampedZoom,
    panX: newPanX,
    panY: newPanY,
  };
}

/**
 * Cursor position relative to container center (for zoom-at-point).
 * Use the container element's getBoundingClientRect(), not the canvas.
 */
export function getCursorPositionFromWheelEvent(
  containerRect: DOMRect,
  clientX: number,
  clientY: number
): { cursorX: number; cursorY: number } {
  const cursorX = clientX - containerRect.left - containerRect.width / 2;
  const cursorY = clientY - containerRect.top - containerRect.height / 2;
  return { cursorX, cursorY };
}
